/**
 * Pure helpers for reading and parsing auth tokens from browser storage.
 *
 * Extracted from client.ts so each helper has CC ≤ 4 and can be tested
 * directly via stubbed Storage instances. The token-source priority is:
 *
 *   1. in-memory Better Auth session token — current tab only
 *   2. localStorage["auth_token:v1"] — persisted API transport token
 *
 * The localStorage read is wrapped in `safeReadStorage` so a thrown `SecurityError`
 * or `QuotaExceededError` (private mode, disabled storage) silently falls
 * through to the next source.
 */

/**
 * Storage version for auth token.
 * Must match the version used in use-auth.ts for consistency.
 */
export const AUTH_TOKEN_STORAGE_VERSION = "v1";
export const AUTH_TOKEN_KEY = `auth_token:${AUTH_TOKEN_STORAGE_VERSION}`;

let inMemoryAuthToken: string | null | undefined;

export function setInMemoryAuthToken(token: string | null): void {
	inMemoryAuthToken = token;
}

export function clearInMemoryAuthToken(): void {
	inMemoryAuthToken = undefined;
}

/**
 * Read a key from a Storage instance, swallowing any thrown error
 * (e.g. SecurityError when cookies-and-site-data is blocked, or
 * QuotaExceededError in private mode). Returns `null` for both
 * "missing key" and "storage unavailable".
 */
export function safeReadStorage(
	storage: Storage | undefined | null,
	key: string,
): string | null {
	if (!storage) {
		return null;
	}
	try {
		return storage.getItem(key);
	} catch {
		return null;
	}
}

/**
 * Read an auth token from the supplied storages in priority order.
 * Returns the first non-empty value found, or `null` if no source yields a token.
 *
 * `windowAvailable` is passed in explicitly so tests don't need to mock
 * the global `window`. In production, callers pass
 * `typeof window !== "undefined"`.
 */
export function readAuthTokenFromStorages(args: {
	windowAvailable: boolean;
	localStorage?: Storage | null;
}): string | null {
	if (inMemoryAuthToken !== undefined) {
		return inMemoryAuthToken;
	}
	if (!args.windowAvailable) {
		return null;
	}
	const fromLocal = safeReadStorage(args.localStorage, AUTH_TOKEN_KEY);
	if (fromLocal) {
		return fromLocal;
	}
	return null;
}
